import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useTranslation } from 'react-i18next'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useHistory } from 'utils/router-compat'
import { routeName } from 'routes/routes-name'
import { selectMe } from 'app/slice/user.slice'
import { canManageTargets } from 'features/executive/permission-store'
import { colors } from 'constants/theme'
import { CustomTab, CustomTabs, LayoutRoot } from './usedStyle'

import RevenueTargetsTab from './RevenueTargetsTab'
import ProcedureTargetsTab from './ProcedureTargetsTab'
import HistoryTab from './HistoryTab'
import AgeGroupsTab from './AgeGroupsTab'
import OccupationGroupsTab from './OccupationGroupsTab'
import PromotionsTab from './PromotionsTab'
import ProcedureTypesTab from './ProcedureTypesTab'

type TargetTab =
  | 'revenue'
  | 'procedure'
  | 'ageGroups'
  | 'occupations'
  | 'promotions'
  | 'procedureTypes'
  | 'history'

const TAB_PATH: Record<TargetTab, string> = {
  revenue: routeName.dashboardTargetsRevenue,
  procedure: routeName.dashboardTargetsProcedure,
  ageGroups: routeName.dashboardTargetsAgeGroups,
  occupations: routeName.dashboardTargetsOccupations,
  promotions: routeName.dashboardTargetsPromotions,
  procedureTypes: routeName.dashboardTargetsProcedureTypes,
  history: routeName.dashboardTargetsHistory,
}

const pathToTab = (p: string): TargetTab => {
  // Check the more specific path first — dashboardTargetsProcedureTypes shares
  // the "/procedure" prefix with dashboardTargetsProcedure.
  if (p.startsWith(routeName.dashboardTargetsProcedureTypes)) return 'procedureTypes'
  if (p.startsWith(routeName.dashboardTargetsProcedure)) return 'procedure'
  if (p.startsWith(routeName.dashboardTargetsAgeGroups)) return 'ageGroups'
  if (p.startsWith(routeName.dashboardTargetsOccupations)) return 'occupations'
  if (p.startsWith(routeName.dashboardTargetsPromotions)) return 'promotions'
  if (p.startsWith(routeName.dashboardTargetsHistory)) return 'history'
  return 'revenue'
}

export default function DashboardTargetsIndex() {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const me = useSelector(selectMe) as any

  if (!canManageTargets()) {
    return <Redirect to={routeName.dashboard} />
  }

  if (location.pathname === routeName.dashboardTargets) {
    return <Redirect to={routeName.dashboardTargetsRevenue} />
  }

  const active = pathToTab(location.pathname)

  return (
    <LayoutRoot sx={{ p: 3 }}>
      {/* Title + tab nav stick to the top of the scroll container so users
          can switch tabs even after scrolling deep into a long table. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: colors.white,
          mx: -3,
          px: 3,
          pt: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tooltip title={t('DASHBOARD_TARGETS.BACK_TO_DASHBOARD', { defaultValue: 'กลับไปแดชบอร์ด' }) as string}>
            <IconButton onClick={() => history.push(routeName.dashboard)} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5">
            {t('DASHBOARD_TARGETS.PAGE_TITLE')}
          </Typography>
        </Box>
        <CustomTabs
          value={active}
          onChange={(_, v: TargetTab) => history.push(TAB_PATH[v])}
          variant="scrollable"
          scrollButtons="auto"
        >
          <CustomTab value="revenue" label={t('DASHBOARD_TARGETS.TAB.REVENUE')} />
          <CustomTab value="procedure" label={t('DASHBOARD_TARGETS.TAB.PROCEDURE')} />
          <CustomTab value="ageGroups" label={t('DASHBOARD_TARGETS.TAB.AGE_GROUPS', 'กลุ่มอายุ')} />
          <CustomTab value="occupations" label={t('DASHBOARD_TARGETS.TAB.OCCUPATIONS', 'อาชีพ')} />
          <CustomTab value="promotions" label={t('DASHBOARD_TARGETS.TAB.PROMOTIONS', 'โปรโมชั่น')} />
          <CustomTab
            value="procedureTypes"
            label={t('DASHBOARD_TARGETS.TAB.PROCEDURE_TYPES', 'ประเภทหัตถการ')}
          />
          <CustomTab value="history" label={t('DASHBOARD_TARGETS.TAB.HISTORY')} />
        </CustomTabs>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Switch>
          <Route exact path={routeName.dashboardTargetsRevenue} component={RevenueTargetsTab} />
          <Route exact path={routeName.dashboardTargetsProcedure} component={ProcedureTargetsTab} />
          <Route exact path={routeName.dashboardTargetsAgeGroups} component={AgeGroupsTab} />
          <Route exact path={routeName.dashboardTargetsOccupations} component={OccupationGroupsTab} />
          <Route exact path={routeName.dashboardTargetsPromotions} component={PromotionsTab} />
          <Route exact path={routeName.dashboardTargetsProcedureTypes} component={ProcedureTypesTab} />
          <Route exact path={routeName.dashboardTargetsHistory} component={HistoryTab} />
        </Switch>
      </Box>
    </LayoutRoot>
  )
}
